import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import Header from '../../components/ui/Header';
import Footer from '../../components/ui/Footer';
import Icon from '../../components/AppIcon';
import Image from '../../components/AppImage';
import Button from '../../components/ui/Button';
import { useWebsiteData } from '../../context/WebsiteDataContext';

const BlogDetail = () => {
    const { blogId } = useParams();
    const navigate = useNavigate();
    const { getPublishedBlogs } = useWebsiteData();
    const blogs = getPublishedBlogs();

    const [blog, setBlog] = useState(null);
    const [relatedBlogs, setRelatedBlogs] = useState([]);

    useEffect(() => {
        // Compare IDs as strings
        const foundBlog = blogs.find(b => String(b.id) === String(blogId));

        if (foundBlog) {
            setBlog(foundBlog);
            // Find related blogs (same category, excluding current)
            const related = blogs
                .filter(b => String(b.id) !== String(blogId) && b.category === foundBlog.category)
                .slice(0, 3);
            setRelatedBlogs(related);
        } else if (blogs.length > 0) {
            // Only redirect if blogs have loaded but not found
            navigate('/blog');
        }
    }, [blogId, blogs, navigate]);

    const formatDate = (dateString) => {
        if (!dateString) return '';
        return new Date(dateString).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'long',
            day: 'numeric'
        });
    };

    if (!blog) {
        return (
            <div className="min-h-screen bg-background">
                <Header />
                <div className="flex items-center justify-center min-h-[60vh]">
                    <div className="text-center">
                        <Icon name="Loader" size={48} className="mx-auto text-primary animate-spin mb-4" />
                        <p className="text-muted-foreground">Loading article...</p>
                    </div>
                </div>
                <Footer />
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-background">
            <Header />

            {/* Breadcrumb */}
            <section className="pt-24 pb-4 bg-muted/30 border-b border-border">
                <div className="container mx-auto px-4">
                    <nav className="flex items-center space-x-2 text-sm text-muted-foreground">
                        <Link to="/" className="hover:text-foreground transition-colors">Home</Link>
                        <Icon name="ChevronRight" size={14} />
                        <Link to="/blog" className="hover:text-foreground transition-colors">Blog</Link>
                        <Icon name="ChevronRight" size={14} />
                        <span className="text-foreground font-medium truncate max-w-[200px]">{blog.title}</span>
                    </nav>
                </div>
            </section>

            {/* Article Header */}
            <section className="py-12 bg-background">
                <div className="container mx-auto px-4 max-w-4xl">
                    <div className="mb-6">
                        <span className="inline-block px-3 py-1 bg-primary/10 text-primary text-sm font-medium rounded-full mb-4">
                            {blog.category}
                        </span>
                        <h1 className="text-3xl md:text-5xl font-bold text-foreground mb-6 leading-tight">
                            {blog.title}
                        </h1>
                        <div className="flex items-center space-x-6 text-muted-foreground">
                            <div className="flex items-center space-x-2">
                                <div className="w-10 h-10 bg-muted rounded-full flex items-center justify-center">
                                    <Icon name="User" size={20} />
                                </div>
                                <div>
                                    <p className="text-sm font-medium text-foreground">{blog.author}</p>
                                    <p className="text-xs">Author</p>
                                </div>
                            </div>
                            <div className="flex items-center space-x-2">
                                <Icon name="Calendar" size={18} />
                                <span className="text-sm">{formatDate(blog.publishDate || blog.createdAt)}</span>
                            </div>
                        </div>
                    </div>

                    {/* Featured Image */}
                    <div className="mb-12 rounded-xl overflow-hidden shadow-lg">
                        <Image
                            src={blog.featuredImage || '/assets/images/no_image.png'}
                            alt={blog.title}
                            className="w-full h-auto object-cover max-h-[500px]"
                        />
                    </div>

                    {/* Content */}
                    <div className="prose prose-lg max-w-none text-foreground/90">
                        {/* Simple paragraph rendering */}
                        {blog.content.split('\n').map((paragraph, idx) => (
                            <p key={idx} className="mb-4">
                                {paragraph}
                            </p>
                        ))}
                    </div>

                    {/* Tags */}
                    {blog.tags && blog.tags.length > 0 && (
                        <div className="mt-12 pt-6 border-t border-border">
                            <div className="flex flex-wrap gap-2">
                                <span className="text-sm font-medium text-muted-foreground mr-2 py-1">Tags:</span>
                                {blog.tags.map((tag, index) => (
                                    <span key={index} className="px-3 py-1 bg-muted text-muted-foreground text-sm rounded-full">
                                        #{tag}
                                    </span>
                                ))}
                            </div>
                        </div>
                    )}
                </div>
            </section>

            {/* Related Posts */}
            {relatedBlogs.length > 0 && (
                <section className="py-16 bg-muted/30">
                    <div className="container mx-auto px-4">
                        <h2 className="text-2xl font-bold text-foreground mb-8 text-center">Related Articles</h2>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                            {relatedBlogs.map((related) => (
                                <Link key={related.id} to={`/blog/${related.id}`} className="group bg-card rounded-lg overflow-hidden shadow-sm hover:shadow-md transition-shadow">
                                    <div className="h-48 overflow-hidden">
                                        <Image
                                            src={related.featuredImage || '/assets/images/no_image.png'}
                                            alt={related.title}
                                            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                                        />
                                    </div>
                                    <div className="p-4">
                                        <p className="text-xs text-primary font-medium mb-2">{related.category}</p>
                                        <h3 className="font-bold text-lg mb-2 line-clamp-2 group-hover:text-primary transition-colors">
                                            {related.title}
                                        </h3>
                                        <p className="text-sm text-muted-foreground line-clamp-2">
                                            {related.excerpt}
                                        </p>
                                    </div>
                                </Link>
                            ))}
                        </div>
                    </div>
                </section>
            )}

            <Footer />
        </div>
    );
};

export default BlogDetail;
